// Remembers which product card a shopper clicked out of an Algolia result grid, which page of
// results that card was on, and where the window was scrolled - so that coming Back from the
// product page lands on that same card instead of at the top of a grid that has reset itself
// to page 0.
//
// One entry at a time: only the most recent grid the shopper left is worth restoring, and it
// is consumed the moment it is used.

const STORAGE_KEY = "algoliaGridRestore";

// Runaway guard, not the normal stopping condition - the replay stops on the saved page. At the
// category grid's hitsPerPage of 50 this ceiling is about 3000 records.
export const MAX_RESTORE_REQUESTS = 60;

// Grid identity = the URL that produced it. Refinements live in the query string, so two
// differently filtered views of the same category never restore into each other.
export const restoreKeyFor = () => `${window.location.pathname}${window.location.search || ""}`;

export function saveGridPosition({ cardId, page }) {
  if (!cardId) return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        key: restoreKeyFor(),
        cardId: String(cardId),
        page: Number(page) || 0,
        scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        savedAt: Date.now(),
      }),
    );
  } catch (e) {
    // private mode / quota - restoring is a nicety, never break the click
  }
}

export function readGridPosition(key) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) return null;

    const entry = JSON.parse(raw);

    if (!entry || entry.key !== key) {
      return null;
    }

    return entry;
  } catch (e) {
    return null;
  }
}

export function clearGridPosition() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // nothing to clean up if session storage is unavailable
  }
}

export function findProductCard(cardId) {
  if (!cardId) return null;
  return document.getElementById(String(cardId));
}

// The card is rendered by an async chain (Algolia response -> accumulated hits -> React commit),
// so it is normally not in the DOM at the moment we start looking. Watch for it the same way the
// plain-JS version did, with a ceiling so a product that never comes back doesn't leak an observer.
export function waitForProductCard(cardId, onFound, { timeout = 5000 } = {}) {
  const existing = findProductCard(cardId);
  if (existing) {
    onFound(existing);
    return () => {};
  }

  const observer = new MutationObserver(() => {
    const el = findProductCard(cardId);
    if (!el) return;
    stop();
    onFound(el);
  });

  const timer = setTimeout(() => {
    stop();
    onFound(null);
  }, timeout);

  let stopped = false;
  function stop() {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    clearTimeout(timer);
  }

  observer.observe(document.body, { childList: true, subtree: true });
  return stop;
}

// Matches the restoredFlash animation in products.css.
const HIGHLIGHT_CLASS = "product-restored";
const HIGHLIGHT_MS = 3000;

// Instant, not smooth: the shopper asked for the page they were on, not for an animated ride
// down 50 products. Repeated because lazy-loaded card images keep resizing rows for a while
// after the commit, which would otherwise leave the target drifting off screen.
export function focusProductCard(el, fallbackScrollY) {
  const scrollToTarget = () => {
    if (el && el.isConnected) {
      const rect = el.getBoundingClientRect();
      const centred = rect.top + window.scrollY - Math.max((window.innerHeight - rect.height) / 2, 0);
      window.scrollTo({ top: Math.max(centred, 0), behavior: "auto" });
    } else if (fallbackScrollY) {
      window.scrollTo({ top: fallbackScrollY, behavior: "auto" });
    }
  };

  scrollToTarget();
  const frame = requestAnimationFrame(scrollToTarget);
  const timers = [setTimeout(scrollToTarget, 250), setTimeout(scrollToTarget, 700)];

  const clearHighlight = () => el && el.classList.remove(HIGHLIGHT_CLASS);
  if (el) {
    el.classList.add(HIGHLIGHT_CLASS);
    timers.push(setTimeout(clearHighlight, HIGHLIGHT_MS));
  }

  return () => {
    cancelAnimationFrame(frame);
    timers.forEach(clearTimeout);
    clearHighlight();
  };
}
